/**
 * HTML-to-DSL Conversion Stage
 *
 * Two-stage pipeline:
 *   Stage 1 (Haiku, non-streaming): Extract global style from HTML/CSS
 *   Stage 2 (Sonnet, streaming): Convert HTML to XML DSL using the extracted style
 */

import type { StylesConfigDto } from '@builder4/shared/dto/styles';
import type {
  StreamCallbacks,
  MessageContent,
  ImageContent,
  TextContent,
  ModelOption,
  AiGenerationStats,
} from '../types';
import {
  nonStreamingCompletion,
  extractJsonFromResponse,
  streamCompletion,
} from '../api/anthropic-client';
import { HTML_STYLE_EXTRACTION_SYSTEM_PROMPT } from '../prompts/html-style-prompt';
import { getHtmlToDslSystemPrompt } from '../prompts/html-to-dsl-prompt';
import { formatGlobalStylesForPrompt } from '../prompts/format-styles';

export interface HtmlStyleResult {
  style: StylesConfigDto;
  label: string;
}

const STYLE_MAX_TOKENS = 8000;
const DSL_MAX_TOKENS = 16384;

/**
 * Preprocess raw HTML for LLM consumption.
 * Extracts <style> content separately, strips scripts/comments/head metadata,
 * and collapses excess whitespace.
 */
export const preprocessHtml = (html: string): { body: string; css: string } => {
  let working = html;

  // Extract <style> tag contents
  const styleBlocks: string[] = [];
  working = working.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (_match, content: string) => {
    styleBlocks.push(content.trim());
    return '';
  });
  const css = styleBlocks.join('\n\n');

  // Strip <script> tags and their content
  working = working.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

  // Strip HTML comments
  working = working.replace(/<!--[\s\S]*?-->/g, '');

  // Strip <head> but preserve <title> text
  working = working.replace(/<head[^>]*>([\s\S]*?)<\/head>/gi, (_match, headContent: string) => {
    const titleMatch = headContent.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
    return titleMatch ? `<!-- title: ${titleMatch[1].trim()} -->` : '';
  });

  // Strip remaining <noscript>, <svg>, <canvas> tags
  working = working.replace(/<noscript[^>]*>[\s\S]*?<\/noscript>/gi, '');
  working = working.replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, '');
  working = working.replace(/<canvas[^>]*>[\s\S]*?<\/canvas>/gi, '');

  // Collapse excess whitespace (multiple newlines → single newline, multiple spaces → single space)
  working = working.replace(/\n{3,}/g, '\n\n');
  working = working.replace(/[ \t]{2,}/g, ' ');
  working = working.trim();

  return { body: working, css };
};

/**
 * Build the user prompt text from preprocessed HTML parts.
 */
const buildUserPromptText = (body: string, css: string): string => {
  if (css) {
    return `CSS:\n${css}\n\nHTML:\n${body}`;
  }
  return body;
};

/**
 * Build a multimodal user prompt when a screenshot is provided,
 * or a plain string prompt when it is not.
 */
const buildUserPrompt = (text: string, screenshotBase64?: string): MessageContent => {
  if (!screenshotBase64) {
    return text;
  }

  const imageBlock: ImageContent = {
    type: 'image',
    source: {
      type: 'base64',
      media_type: inferMediaType(screenshotBase64),
      data: screenshotBase64,
    },
  };

  const textBlock: TextContent = { type: 'text', text };

  return [imageBlock, textBlock];
};

/**
 * Infer MIME type from the first bytes of a base64-encoded image.
 * Falls back to image/png when detection is inconclusive.
 */
const inferMediaType = (base64: string): ImageContent['source']['media_type'] => {
  const prefix = base64.slice(0, 16);
  if (prefix.startsWith('/9j/')) return 'image/jpeg';
  if (prefix.startsWith('R0lGOD')) return 'image/gif';
  if (prefix.startsWith('UklGR')) return 'image/webp';
  return 'image/png';
};

/**
 * Stage 1: Extract global style from HTML (Haiku, non-streaming)
 */
export const extractStyleFromHtml = async (
  html: string,
  signal?: AbortSignal,
  screenshotBase64?: string,
  model?: ModelOption,
  stats?: AiGenerationStats,
): Promise<HtmlStyleResult> => {
  const { body, css } = preprocessHtml(html);
  const promptText = buildUserPromptText(body, css);
  const userPrompt = buildUserPrompt(promptText, screenshotBase64);

  const result = await nonStreamingCompletion({
    model: model ?? 'haiku-4.5',
    maxTokens: STYLE_MAX_TOKENS,
    systemPrompt: HTML_STYLE_EXTRACTION_SYSTEM_PROMPT,
    userPrompt,
    signal,
  });
  if (stats) {
    stats.totalInputTokens += result.inputTokens;
    stats.totalOutputTokens += result.outputTokens;
  }

  const jsonText = extractJsonFromResponse(result.text);
  const parsed = JSON.parse(jsonText) as HtmlStyleResult;

  if (!parsed.label || !parsed.style) {
    throw new Error('Invalid style extraction response: missing label or style');
  }

  return parsed;
};

/**
 * Stage 2: Stream HTML-to-DSL conversion (Sonnet, streaming)
 */
export const streamHtmlToDsl = async (
  html: string,
  style: StylesConfigDto,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
  screenshotBase64?: string,
  model?: ModelOption,
): Promise<void> => {
  const { body, css } = preprocessHtml(html);
  const formattedStyles = formatGlobalStylesForPrompt(style);
  const promptText = `Convert this HTML to XML DSL:\n\n${buildUserPromptText(body, css)}`;
  const userPrompt = buildUserPrompt(promptText, screenshotBase64);

  await streamCompletion(
    {
      model: model ?? 'sonnet-4.6',
      maxTokens: DSL_MAX_TOKENS,
      systemPrompt: getHtmlToDslSystemPrompt(formattedStyles),
      userPrompt,
      signal,
    },
    callbacks,
  );
};
